import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { billingApiService } from '../api/billing-api';
import {
  Invoice,
  InvoiceFilters,
  InvoiceStats,
  CreateInvoiceDto,
  InvoiceStatus,
  BillingState,
} from '../types/admin.types';

// Función para calcular estadísticas basadas en las facturas
const calculateStats = (invoices: Invoice[]): InvoiceStats => {
  const stats: InvoiceStats = {
    total: invoices.length,
    paid: invoices.filter(inv => inv.status === InvoiceStatus.PAID).length,
    pending: invoices.filter(inv => inv.status === InvoiceStatus.PENDING).length,
    overdue: invoices.filter(inv => inv.status === InvoiceStatus.OVERDUE).length,
    cancelled: invoices.filter(inv => inv.status === InvoiceStatus.CANCELLED).length,
    totalAmount: invoices.reduce((sum, inv) => sum + Number(inv.amount || 0), 0),
    paidAmount: invoices
      .filter(inv => inv.status === InvoiceStatus.PAID)
      .reduce((sum, inv) => sum + Number(inv.amount || 0), 0),
    pendingAmount: invoices
      .filter(inv => inv.status === InvoiceStatus.PENDING)
      .reduce((sum, inv) => sum + Number(inv.amount || 0), 0),
    overdueAmount: invoices
      .filter(inv => inv.status === InvoiceStatus.OVERDUE)
      .reduce((sum, inv) => sum + Number(inv.amount || 0), 0),
  };

  return stats;
};

// Estado inicial
const initialBillingState: BillingState = {
  invoices: [],
  currentInvoice: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
  },
  filters: {},
  stats: {
    total: 0,
    paid: 0,
    pending: 0,
    overdue: 0,
    cancelled: 0,
    totalAmount: 0,
    paidAmount: 0,
    pendingAmount: 0,
    overdueAmount: 0,
  },
};

// Async Thunks para Facturas
export const fetchInvoices = createAsyncThunk(
  'billing/fetchInvoices',
  async (filters: InvoiceFilters = {}, { rejectWithValue }) => {
    try {
      const response = await billingApiService.getAllInvoices(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar facturas'
      );
    }
  }
);

export const fetchInvoice = createAsyncThunk(
  'billing/fetchInvoice',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await billingApiService.getInvoice(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar factura'
      );
    }
  }
);

export const createInvoice = createAsyncThunk(
  'billing/createInvoice',
  async (data: CreateInvoiceDto, { rejectWithValue }) => {
    try {
      const response = await billingApiService.createInvoice(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al crear factura'
      );
    }
  }
);

export const updateInvoice = createAsyncThunk(
  'billing/updateInvoice',
  async (
    { id, data }: { id: string; data: Partial<CreateInvoiceDto> },
    { rejectWithValue }
  ) => {
    try {
      const response = await billingApiService.updateInvoice(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al actualizar factura'
      );
    }
  }
);

export const deleteInvoice = createAsyncThunk(
  'billing/deleteInvoice',
  async (id: string, { rejectWithValue }) => {
    try {
      await billingApiService.deleteInvoice(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al eliminar factura'
      );
    }
  }
);

export const markInvoiceAsPaid = createAsyncThunk(
  'billing/markInvoiceAsPaid',
  async (
    { id, paymentMethod }: { id: string; paymentMethod?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await billingApiService.markAsPaid(id, paymentMethod);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al marcar factura como pagada'
      );
    }
  }
);

export const fetchInvoiceStats = createAsyncThunk(
  'billing/fetchInvoiceStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await billingApiService.getInvoiceStats();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar estadísticas'
      );
    }
  }
);

// Slice
const billingSlice = createSlice({
  name: 'billing',
  initialState: initialBillingState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    setCurrentInvoice: (state, action: PayloadAction<Invoice | null>) => {
      state.currentInvoice = action.payload;
    },
    setFilters: (state, action: PayloadAction<InvoiceFilters>) => {
      state.filters = action.payload;
    },
    setPagination: (
      state,
      action: PayloadAction<{ page: number; limit: number; total: number }>
    ) => {
      state.pagination = action.payload;
    },
    clearInvoices: state => {
      state.invoices = [];
      state.currentInvoice = null;
    },
  },
  extraReducers: builder => {
    // Facturas
    builder
      .addCase(fetchInvoices.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        state.loading = false;
        const invoicesData = (action.payload as any)?.data || action.payload;
        state.invoices = Array.isArray(invoicesData) ? invoicesData : [];
        state.pagination.total = state.invoices.length;
        state.stats = calculateStats(state.invoices);
      })
      .addCase(fetchInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchInvoice.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvoice.fulfilled, (state, action) => {
        state.loading = false;
        const invoiceData = (action.payload as any)?.data || action.payload;
        state.currentInvoice = invoiceData;
      })
      .addCase(fetchInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createInvoice.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createInvoice.fulfilled, (state, action) => {
        state.loading = false;
        const invoiceData = (action.payload as any)?.data || action.payload;
        if (!Array.isArray(state.invoices)) {
          state.invoices = [];
        }
        state.invoices.push(invoiceData);
        state.pagination.total += 1;
        state.stats = calculateStats(state.invoices);
      })
      .addCase(createInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateInvoice.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateInvoice.fulfilled, (state, action) => {
        state.loading = false;
        const invoiceData = (action.payload as any)?.data || action.payload;
        if (!Array.isArray(state.invoices)) {
          state.invoices = [];
        }
        const index = state.invoices.findIndex(
          invoice => invoice.id === invoiceData.id
        );

        if (index !== -1) {
          state.invoices[index] = invoiceData;
        }
        if (state.currentInvoice?.id === invoiceData.id) {
          state.currentInvoice = invoiceData;
        }
        state.stats = calculateStats(state.invoices);
      })
      .addCase(updateInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteInvoice.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteInvoice.fulfilled, (state, action) => {
        state.loading = false;
        if (!Array.isArray(state.invoices)) {
          state.invoices = [];
        }
        state.invoices = state.invoices.filter(
          invoice => invoice.id !== action.payload
        );
        state.pagination.total -= 1;
        if (state.currentInvoice?.id === action.payload) {
          state.currentInvoice = null;
        }
        state.stats = calculateStats(state.invoices);
      })
      .addCase(deleteInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(markInvoiceAsPaid.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(markInvoiceAsPaid.fulfilled, (state, action) => {
        state.loading = false;
        const invoiceData = (action.payload as any)?.data || action.payload;
        if (!Array.isArray(state.invoices)) {
          state.invoices = [];
        }
        const index = state.invoices.findIndex(
          invoice => invoice.id === invoiceData.id
        );

        if (index !== -1) {
          state.invoices[index] = invoiceData;
        }
        if (state.currentInvoice?.id === invoiceData.id) {
          state.currentInvoice = invoiceData;
        }
        state.stats = calculateStats(state.invoices);
      })
      .addCase(markInvoiceAsPaid.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchInvoiceStats.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvoiceStats.fulfilled, (state, action) => {
        state.loading = false;
        const statsData = (action.payload as any)?.data || action.payload;
        state.stats = statsData;
      })
      .addCase(fetchInvoiceStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  setCurrentInvoice,
  setFilters,
  setPagination,
  clearInvoices,
} = billingSlice.actions;

export default billingSlice.reducer;







